import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import Logo from '../assets/dialogos/LogoCiclo.svg';
import Colors from 'enums/Colors';
import { FaArrowRight } from 'react-icons/fa6';
import { useHistory } from 'react-router-dom';
import { IsCustomMaxWidth, IsMobileBiggerWidth } from 'core/util/WindowSize';
import { TbLock } from 'react-icons/tb';
import { Popover } from 'antd';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { fetchStorageFile } from 'hooks/StoreFiles';

const Styled = {
    Container: styled.div<{ isMobile: boolean }>`
        display: flex;
        height: auto;
        max-width: 1440px;
        padding: ${({ isMobile }) => (isMobile ? '0px' : '37px 73px 37px 73px')};
        flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
        column-gap: 20px;
        align-self: center;
        align-items: center;
        justify-content: space-evenly;
    `,

    Content: styled.div<{ isMobile: boolean }>`
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 1rem;
        padding: ${({ isMobile }) => (isMobile ? '32px 16px 32px 16px' : '0px')};
    `,

    Img: styled.img<{ isMobile: boolean }>`
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: ${({ isMobile }) => (isMobile ? '0px' : '4px')};
    `,

    Title: styled.h1`
        font-family: 'Barlow';
        font-size: 34px;
        font-weight: 700;
        line-height: 51px;
        letter-spacing: -0.019em;
        text-align: left;
    `,

    Description: styled.p`
        font-family: 'Barlow';
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.019em;
        text-align: left;
        margin-bottom: 20px;
    `,

    ColumnsContainer: styled.div`
        display: flex;
        flex-direction: row;
        gap: 20px;
        flex-wrap: wrap;
    `,

    ColumnsContainerSingle: styled.div`
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
    `,

    Column: styled.div<{ fullWidth?: boolean }>`
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    `,

    Item: {
        Container: styled.div<{ fullWidth?: boolean }>`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: ${({ fullWidth }) => (fullWidth ? '100%' : '100%')};
            max-width: ${({ fullWidth }) => (fullWidth ? 'none' : '420px')};
            height: 60px;
            padding: 10px;
            padding-left: 20px;
            padding-right: 20px;
            border-radius: 4px;
            margin-top: 0px;
            margin-bottom: 0px;
            background-color: ${Colors.Blue};
            color: ${Colors.White};
            cursor: pointer;
        `,

        Content: styled.div`
            display: flex;
            align-items: center;
            gap: 1rem;
            cursor: pointer;
            margin-left: 10px;

            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

interface IItem {
    title: ReactNode;
    url: string;
    isBlock?: boolean;
    titleIsBlock?: string;
}

const chunkArray = (array: IItem[], size: number): IItem[][] => {
    const chunks: IItem[][] = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

const Item = ({ title, url, isBlock, titleIsBlock }: IItem) => {
    const history = useHistory();
    const [openPopOver, setPopOver] = useState<boolean>(false);

    return (
        <Popover
            placement="topRight"
            open={openPopOver}
            content={
                <>
                    <p dangerouslySetInnerHTML={{ __html: titleIsBlock ?? '' }} />
                </>
            }
            title={
                <span>
                    Indisponível <TbLock />
                </span>
            }
        >
            <Styled.Item.Container
                onMouseEnter={() => isBlock && setPopOver(true)}
                onMouseLeave={() => isBlock && setPopOver(false)}
                onClick={() => {
                    !isBlock && history.push(url);
                }}
            >
                <p>{title}</p>
                <Styled.Item.Content
                    onClick={() => {
                        !isBlock && history.push(url);
                    }}
                >
                    {isBlock ? (
                        <>
                            Indisponível <TbLock />
                        </>
                    ) : (
                        <>
                            <MdOutlineOpenInNew />
                        </>
                    )}
                </Styled.Item.Content>
            </Styled.Item.Container>
        </Popover>
    );
};

interface ICicloExclusivo {
    title: string;
    description: string;
    itens: IItem[];
    img: string;
    singleColumn?: boolean;
}

const CicloExclusivo = ({ title, description, itens, img, singleColumn = false }: ICicloExclusivo) => {
    const isMobile = IsMobileBiggerWidth();
    const imgStorage = fetchStorageFile(img);
    const useSingleColumnLayout = singleColumn && !isMobile;

    if (isMobile) {
        return (
            <Styled.Container isMobile={isMobile}>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <Styled.Img isMobile={isMobile} src={imgStorage} />
                </div>
                <Styled.Content isMobile={isMobile}>
                    <Styled.Title>{`${title}`}</Styled.Title>
                    <Styled.Description
                        dangerouslySetInnerHTML={{
                            __html: description,
                        }}
                    />
                    {itens &&
                        itens.map((dados, index) => (
                            <Item
                                key={index}
                                title={dados.title}
                                url={dados.url}
                                isBlock={dados.isBlock}
                                titleIsBlock={dados.titleIsBlock}
                            />
                        ))}
                </Styled.Content>
            </Styled.Container>
        );
    } else {
        const itemChunks = useSingleColumnLayout ? [itens || []] : chunkArray(itens || [], 5);
        return (
            <Styled.Container isMobile={isMobile}>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <Styled.Img isMobile={isMobile} src={imgStorage} />
                </div>
                <Styled.Content isMobile={isMobile}>
                    <Styled.Title>{`${title}`}</Styled.Title>
                    <Styled.Description
                        dangerouslySetInnerHTML={{
                            __html: description,
                        }}
                    />
                    {useSingleColumnLayout ? (
                        <Styled.ColumnsContainerSingle>
                            {itemChunks.map((chunk, chunkIndex) => (
                                <Styled.Column key={chunkIndex} fullWidth>
                                    {chunk.map((dados, index) => (
                                        <Item
                                            key={index}
                                            title={dados.title}
                                            url={dados.url}
                                            isBlock={dados.isBlock}
                                            titleIsBlock={dados.titleIsBlock}
                                        />
                                    ))}
                                </Styled.Column>
                            ))}
                        </Styled.ColumnsContainerSingle>
                    ) : (
                        <Styled.ColumnsContainer>
                        {itemChunks.map((chunk, chunkIndex) => (
                            <Styled.Column key={chunkIndex}>
                                {chunk.map((dados, index) => (
                                    <Item
                                        key={index}
                                        title={dados.title}
                                        url={dados.url}
                                        isBlock={dados.isBlock}
                                        titleIsBlock={dados.titleIsBlock}
                                    />
                                ))}
                            </Styled.Column>
                        ))}
                        </Styled.ColumnsContainer>
                    )}
                </Styled.Content>
            </Styled.Container>
        );
    }
};

export default CicloExclusivo;
